import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import { auth } from '../firebase';
import { useContact } from '../contexts/ContactContext';
import SEO from '../components/SEO';

const Fees = () => {
  const { firestoreUser } = useContext(UserContext);
  const currentAuthUser = auth.currentUser;
  const { openContact } = useContact();
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show/hide "Back to Top" button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column' }}>
      <SEO
        title="Fees"
        description="PedalxChange fee structure for trading and purchasing guitar pedals. Transparent pricing with low platform fees and escrow protection."
        url="https://pedalxchange.com/fees"
        keywords="pedal trading fees, guitar pedal marketplace fees, PedalxChange pricing, trade fees, purchase fees"
      />
      {/* Header Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '0.65rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div className="fees-content-container" style={{ textAlign: 'center', marginBottom: '1.3rem', width: '100%' }}>
          <h1 className="text-3xl font-bold text-gray-900" style={{ textAlign: 'center', fontSize: '1.875rem', marginBottom: '1rem', lineHeight: '1.2' }}>
            Play More. Spend less.
          </h1>
          </div>
        </div>
        <style jsx>{`
          @media (max-width: 768px) {
            .fees-content-container {
              width: 100% !important;
              padding: 0 1rem !important;
              box-sizing: border-box !important;
            }
            .fees-content-container h1 {
              max-width: 100% !important;
              word-wrap: break-word !important;
              font-size: 1.5rem !important;
              text-align: center !important;
            }
            .fees-content-container h2 {
              font-size: 1.25rem !important;
            }
            .fees-content-container h3 {
              font-size: 1rem !important;
            }
            .fees-content-container p {
              font-size: 0.9rem !important;
            }
            .fees-content-container .fees-list {
              font-size: 0.9rem !important;
              margin-left: 0rem !important;
            }
            .fees-content-container .main-bullet {
              margin-left: 0rem !important;
            }
            .fees-content-container .sub-bullet {
              margin-left: 1rem !important;
            }
            .fees-content-container .sub-bullet.sub-bullet-indent {
              margin-left: 2rem !important;
            }
            .responsive-svg {
              margin-top: 0rem !important;
              max-width: 105% !important;
              padding: 0 !important;
              height: auto !important;
              margin-left: -0.5rem !important;
              margin-right: auto !important;
              display: block !important;
            }
            .svg-container {
              width: 100% !important;
              max-width: 100% !important;
              text-align: center !important;
              margin: 0 auto !important;
              margin-left: 0 !important;
            }
            /* Fix horizontal scrolling by ensuring all containers fit mobile */
            .max-w-7xl {
              padding-left: 1rem !important;
              padding-right: 1rem !important;
            }
            /* Ensure all content containers are mobile-friendly */
            div[style*="width: 800px"], div[style*="width: 600px"], div[style*="width: 400px"] {
              width: 100% !important;
              max-width: 100% !important;
              padding: 0 1rem !important;
              box-sizing: border-box !important;
            }
          }
        `}</style>
      </div>

      {/* Fees Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '0rem', paddingBottom: '2rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div className="fees-content-container" style={{ textAlign: 'left', width: '100%', maxWidth: '800px', boxSizing: 'border-box', marginLeft: 'auto', marginRight: 'auto' }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Trading Fees
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              List for free and only pay when you complete a trade. We charge a small $3 fee per successful trade, but our monthly memberships offer unlimited, free trading. Choose from three options to get started.
            </h3>
            
            <ul className="text-lg font-semibold text-gray-700 fees-list" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li className="main-bullet" style={{ marginBottom: '0.5rem' }}>
                <strong>Standard free membership tier: $0/mo</strong>
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Complete a trade: $3 (plus $1 for each additional pedal beyond the first in a multi-pedal trade)
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                6% fee on the cash portion of a trade (if applicable)
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                (Optional) $5.00 TradeProtect fee for reimbursement up to $250 on qualifying trades
              </li>
              <li className="main-bullet" style={{ marginBottom: '0.5rem' }}>
                <strong>9V membership tier: $5/mo</strong>
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Unlimited free trades
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Reduced 4% fee on the cash portion of a trade (if applicable)
              </li>
              <li className="main-bullet" style={{ marginBottom: '0.5rem' }}>
                <strong>18V membership tier: $9/mo</strong>
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Unlimited free trades
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Promoted matches bring your pedals to the top of everyones match list
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Reduced 4% fee on the cash portion of a trade (if applicable)
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Reduced (optional) $3.00 TradeProtect fee for reimbursement up to $250 on qualifying trades
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Bump all your active listings to the top of the Browse feed, weekly
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Buying and Selling Fees Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '0rem', paddingBottom: '2rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div className="fees-content-container" style={{ textAlign: 'left', width: '100%', maxWidth: '800px', boxSizing: 'border-box', marginLeft: 'auto', marginRight: 'auto' }}>
            <h2 style={{ 
              color: '#C9A227', 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              Buying and Selling Fees
            </h2>
            
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              List for free and only pay when you complete a sale. We charge a small $3 fee per successful sale, but our monthly memberships offer unlimited, free selling and trading. Choose from three options to get started.
            </h3>
            
            <ul className="text-lg font-semibold text-gray-700 fees-list" style={{ 
              marginLeft: '1.5rem',
              textAlign: 'left'
            }}>
              <li className="main-bullet" style={{ marginBottom: '0.5rem' }}>
                <strong>Standard free membership tier: $0/mo</strong>
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Seller: $3 listing completion fee
              </li>
              <li className="sub-bullet sub-bullet-indent" style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                (Optional) $5.00 TradeProtect fee for reimbursement up to $250 on qualifying sales
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Buyer: 6% fee on the cash portion of a purchase
              </li>
              <li className="main-bullet" style={{ marginBottom: '0.5rem' }}>
                <strong>9V membership tier: $5/mo</strong>
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Seller: Unlimited free sales and trades
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Buyer: Reduced 4% fee on the cash portion of a trade (if applicable)
              </li>
              <li className="main-bullet" style={{ marginBottom: '0.5rem' }}>
                <strong>18V membership tier: $9/mo</strong>
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Seller: Unlimited free sales and trades
              </li>
              <li className="sub-bullet sub-bullet-indent" style={{ marginBottom: '0.5rem', marginLeft: '2rem' }}>
                Reduced (optional) $3.00 TradeProtect fee for reimbursement up to $250 on qualifying trades
              </li>
              <li className="sub-bullet" style={{ marginBottom: '0.5rem', marginLeft: '1rem' }}>
                Reduced 4% fee on the cash portion of a trade (if applicable)
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Example Section */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div className="fees-content-container" style={{ textAlign: 'left', width: '100%', maxWidth: '600px', boxSizing: 'border-box', marginLeft: 'auto', marginRight: 'auto' }}>
            <h3 style={{ 
              color: '#6b7280', 
              fontSize: '1.125rem', 
              fontWeight: 'bold', 
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              Example: Trade with Cash Offer Exchange
            </h3>
          </div>
          
          {/* SVG Section */}
          <div className="svg-container" style={{ width: '100%', maxWidth: '600px', textAlign: 'center', margin: '0 auto', boxSizing: 'border-box' }}>
            <img 
              src="/images/fees.svg" 
              alt="Fees Example Guide"
              className="responsive-svg"
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                backgroundColor: 'transparent',
                mixBlendMode: 'multiply',
                marginTop: '0rem',
                display: 'block',
                pointerEvents: 'none'
              }}
            />
          </div>
        </div>
      </div>

      {/* Return to previous page link */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '2rem', paddingBottom: '2rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center hover:opacity-80 transition-opacity"
              style={{ 
                fontSize: '17px', 
                textDecoration: 'none', 
                color: '#5A9ECB', 
                fontWeight: 'bold',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '0'
              }}
            >
              <span style={{ marginRight: '8px', fontSize: '19px', fontWeight: 'bold' }}>←</span>
              Return to previous page
            </button>
          </div>
        </div>
      </div>

      {/* Sign Up Button */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8" style={{ paddingTop: '0rem', paddingBottom: '2rem', paddingLeft: '1.5rem', paddingRight: '1.5rem', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '1100px', margin: '0 auto', width: '100%', justifyContent: 'center', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            {!firestoreUser && (
              <Link 
                to="/signup" 
                className="hover:opacity-80 transition-opacity"
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  backgroundColor: '#C9A227',
                  borderRadius: '9999px',
                  border: 'none',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}
              >
                Sign Up Here
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="bg-gray-100 border-t border-gray-200 py-4 fees-footer" style={{ paddingBottom: '10px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
        <style jsx>{`
          @media (max-width: 768px) {
            .fees-footer {
              padding-bottom: 90px !important;
            }
          }
        `}</style>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
    </div>
  );
};

export default Fees;